import { Button, Card, Divider, Icons, Stack, Table, Tooltip, Typography } from '@healthinal/ui';
import { differenceInMilliseconds } from 'date-fns';
import { useState } from 'react';
import {
  getAppointmentDate,
  getAppointmentStartDateTime,
  getAppointmentTimeFrom,
} from '../../../../../api/created/appointment.ts';
import { useGetAppointmentsSuspense } from '../../../../../api/generated/appointment-controller/appointment-controller.ts';
import { useGetTracingContactAttemptsByPatientSuspense } from '../../../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import { AppointmentDtoType } from '../../../../../api/generated/model';
import { formatDateTime, formatDateWithGermanMonth, formatDateWithOptionalTime } from '../../../../../utils/date.ts';
import { AppointmentDetailsDialog } from '../../../../components/appointment/AppointmentDetailsDialog.tsx';
import { AddContactAttemptDialog } from './AddContactAttemptDialog.tsx';
import { SendSmsDialog } from './SendSmsDialog.tsx';

export function ContactAttemptsList({ patientId }: { patientId: string }) {
  const { data: attempts } = useGetTracingContactAttemptsByPatientSuspense(patientId);
  const { data: appointments } = useGetAppointmentsSuspense({ patientId });
  const [isAddAttemptDialogOpen, setIsAddAttemptDialogOpen] = useState(false);
  const [isSendSmsDialogOpen, setIsSendSmsDialogOpen] = useState(false);
  const [isAppointmentDetailsDialogOpen, setIsAppointmentDetailsDialogOpen] = useState(false);

  const bloodTestAppointment = appointments
    .filter((a) => a.type === AppointmentDtoType.BLOOD_SAMPLING)
    .sort((a, b) =>
      differenceInMilliseconds(getAppointmentStartDateTime(b) ?? '', getAppointmentStartDateTime(a) ?? ''),
    )[0];

  return (
    <>
      <Card>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography level="title-lg">Kontaktversuche</Typography>

          <Stack direction={'row'} gap={1}>
            <Button onClick={() => setIsAddAttemptDialogOpen(true)} endDecorator={<Icons.Add />}>
              Versuch hinzufügen
            </Button>
            <Button color={'neutral'} onClick={() => setIsSendSmsDialogOpen(true)} endDecorator={<Icons.Sms />}>
              SMS senden
            </Button>
          </Stack>
        </Stack>
        <Divider />

        {attempts.length || bloodTestAppointment ? (
          <Table>
            <thead>
              <tr>
                <th style={{ width: 180 }}>Zeitpunkt</th>
                <th>Durch</th>
                <th>Notiz</th>
              </tr>
            </thead>
            <tbody>
              {bloodTestAppointment && (
                <tr
                  onClick={() => setIsAppointmentDetailsDialogOpen(true)}
                  style={{
                    backgroundColor: 'var(--joy-palette-success-softBg)',
                    color: 'var(--joy-palette-success-softColor)',
                  }}>
                  <td>{formatDateWithGermanMonth(getAppointmentDate(bloodTestAppointment))}</td>
                  <td>
                    {bloodTestAppointment.createdBy?.lastName} {bloodTestAppointment.createdBy?.firstName}
                  </td>
                  <td>
                    Bluttest vereinbart für den{' '}
                    {formatDateWithOptionalTime(
                      getAppointmentDate(bloodTestAppointment) ?? '',
                      getAppointmentTimeFrom(bloodTestAppointment),
                    )}
                  </td>
                </tr>
              )}
              {[...attempts]
                .sort((a, b) => differenceInMilliseconds(b.datetime, a.datetime))
                .map((attempt) => (
                  <tr key={attempt.id}>
                    <td>{formatDateTime(attempt.datetime)}</td>
                    <td>
                      {attempt.createdBy?.lastName} {attempt.createdBy?.firstName}
                    </td>
                    <td>
                      {attempt.smsText ? (
                        <>
                          SMS verschickt
                          {attempt.smsRecipientContact && ' an ' + attempt.smsRecipientContact?.name}
                          <Tooltip title={attempt.smsText} variant="plain">
                            <Icons.Sms size={'sm'} sx={{ ml: 1, pt: 0.5 }} />
                          </Tooltip>
                        </>
                      ) : (
                        attempt.note
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        ) : (
          <Stack alignItems={'center'} gap={1} p={3}>
            <Icons.Description />
            <Typography level="body-sm">Noch keine Kontaktversuche vorhanden.</Typography>
          </Stack>
        )}
      </Card>
      <AddContactAttemptDialog
        open={isAddAttemptDialogOpen}
        onClose={() => setIsAddAttemptDialogOpen(false)}
        patientId={patientId}
      />
      <SendSmsDialog open={isSendSmsDialogOpen} onClose={() => setIsSendSmsDialogOpen(false)} patientId={patientId} />
      {bloodTestAppointment && (
        <AppointmentDetailsDialog
          open={isAppointmentDetailsDialogOpen}
          onClose={() => setIsAppointmentDetailsDialogOpen(false)}
          appointmentId={bloodTestAppointment.id}
        />
      )}
    </>
  );
}
